// Google Maps "directions" link (documented Maps URLs format — no API key).
// Only the destination is given: Google fills in the engineer's current
// location as the start itself, so we never read or send it. Opens the Google
// Maps app on a phone that has it, otherwise Google Maps in the browser.
const GOOGLE_MAPS_DIRECTIONS_URL = 'https://www.google.com/maps/dir/?api=1&destination='

interface DirectionsDestination {
  /** Business name, e.g. "Wolseley Eastleigh" — lands on its Google listing. */
  name: string | null | undefined
  /** Street address and postcode; at least one is needed for a text search. */
  address: string | null | undefined
  postcode: string | null | undefined
  /** Coordinates — used only when there's no address to search for. */
  latitude: number | null | undefined
  longitude: number | null | undefined
}

/**
 * Build a Google Maps directions link from the viewer's location to a place.
 *
 * Searches for the business by name + address + postcode: Google then lands on
 * that business's own listing. Bare coordinates were avoided because Google
 * labels a point with the nearest business it knows, so on a trading estate a
 * slightly-off point routed to a neighbouring merchant. The postcode keeps the
 * search local. Coordinates are the fallback when there's no address at all.
 *
 * @returns The link, or null when there is nothing to navigate to.
 */
export function googleMapsDirectionsUrl({
  name,
  address,
  postcode,
  latitude,
  longitude,
}: DirectionsDestination): string | null {
  if (address?.trim() || postcode?.trim()) {
    const query = [name, address, postcode]
      .map((part) => part?.trim())
      .filter(Boolean)
      .join(', ')
    return `${GOOGLE_MAPS_DIRECTIONS_URL}${encodeURIComponent(query)}`
  }
  if (latitude != null && longitude != null) {
    return `${GOOGLE_MAPS_DIRECTIONS_URL}${latitude},${longitude}`
  }
  return null
}
